import { UrlMatcher, UrlMatchResult, UrlSegment } from '@angular/router';

const HTML_SUFFIX = '.html';

/** 移除存在 html 後綴的字串 */
const removeHtmlSuffix = (segmentPath: string) =>
  segmentPath.endsWith(HTML_SUFFIX) ? segmentPath.replace(HTML_SUFFIX, '') : segmentPath;

/** 如果存在 html 後綴或者是空字串，則不加上 .html，否則加上後綴 */
const handleHtmlSuffix = (segmentPath: string) =>
  segmentPath && !segmentPath.endsWith(HTML_SUFFIX) ? segmentPath + HTML_SUFFIX : (segmentPath ?? '');

const createUrlMatcherResult = (
  consumed: UrlSegment[],
  posParams?: Record<string, UrlSegment>,
) => ({
  consumed,
  ...(posParams && { posParams })
});

/**
 * 產生一個UrlMatcher，可以匹配到指定的固定路徑（無論有沒有包含.html後綴）
 * @param path 欲匹配的固定基礎路徑
 */
function createStaticPathUrlMatcher(path: string): UrlMatcher {
  return (segments: UrlSegment[]): UrlMatchResult => {
    // 當切割後的網址區段(用斜線進行切割)只有一個時，代表目前瀏覽的頁面是目前的頁面
    // 同時segment[0]的path必須符合指定的path（無論有沒有包含.html後綴）
    if (segments.length === 1 && RegExp(`^${path}(\\.html)?`, 'g').exec(segments[0].path)) {
      const [segment] = segments;
      const { path: segmentPath, parameters } = segment;
      const modifiedSegmentPath = handleHtmlSuffix(segmentPath);
      // 改動匹配的網址區段，補上.html結尾（由於是目前頁面，所以需要）
      const modifiedSegment = new UrlSegment(modifiedSegmentPath, parameters);
      return createUrlMatcherResult([modifiedSegment]);
    }
    return null;
  };
}

/**
 * 產生一個UrlMatcher，可以匹配到使用變數參數的路徑
 *
 * 比如選單/詳細頁等等，並將該參數抽取出來供ParamMap使用
 * @param paramKey 欲匹配的參數鍵名稱
 */
function createParameterizedPathUrlMatcher(paramKey: string): UrlMatcher {
  if (!paramKey) { throw new Error('paramKey is required'); }
  return (segments: UrlSegment[]): UrlMatchResult => {
    if (!segments.length) {
      return null;
    }
    const [segment] = segments;
    const { path: segmentPath, parameters } = segment;
    /** 判斷目前瀏覽的頁面是當前頁面還是是子頁面 */
    const isChildPage = segments.length > 1;
    const sliceParam = removeHtmlSuffix(segmentPath);
    const modifiedSegmentPath = isChildPage ? sliceParam : handleHtmlSuffix(segmentPath);
    return createUrlMatcherResult([new UrlSegment(modifiedSegmentPath, parameters)], { [paramKey]: new UrlSegment(sliceParam, {}) });
  };
}

/**
 * 產生一個UrlMatcher，可以匹配到使用多個變數參數的路徑
 * 比如 :path/:lang
 * @param paramKeys 欲匹配的參數鍵名稱陣列，假設原先path規則輸入的路由是':path/:lang'，那麼使用UrlMatcher時，paramKeys就會是['path', 'lang']
 * @returns
 */
function createParameterizedPathsUrlMatcher(paramKeys: string[]): UrlMatcher {
  if (!Array.isArray(paramKeys) || !paramKeys.length) {
    throw new Error('paramKeys is required and must be an array with at least one element');
  }
  return (segments: UrlSegment[]): UrlMatchResult => {
    const paramKeyLength = paramKeys.length;
    if (!segments.length || segments.length < paramKeyLength || segments.some(s => !s.path || s.path === '.html')) {
      return null;
    }
    const relevantSegments = segments.slice(0, paramKeyLength);
    const isExactMatch = segments.length === paramKeyLength;
    const modifiedSegments = relevantSegments.map((segment, index) => {
      const { path: segmentPath, parameters } = segment;
      const isLast = index === paramKeyLength - 1;
      const processedPath = isExactMatch && isLast
        ? handleHtmlSuffix(segmentPath)
        : removeHtmlSuffix(segmentPath);
      return new UrlSegment(processedPath, parameters);
    });

    const posParams: Record<string, UrlSegment> = paramKeys.reduce((acc, key, index) => ({
      ...acc,
      [key]: new UrlSegment(removeHtmlSuffix(modifiedSegments[index].path), modifiedSegments[index].parameters),
    }), {});
    console.log('createParameterizedPathsUrlMatcher: Done');
    return createUrlMatcherResult(modifiedSegments, posParams);
  };
}

export const URL_MATCHERS = {
  /** 404頁面的UrlMatcher */
  notfound: createStaticPathUrlMatcher('404'),
  /** sitemap頁面的UrlMatcher */
  sitemap: createStaticPathUrlMatcher('sitemap'),
  /** search頁面的UrlMatcher */
  search: createStaticPathUrlMatcher('search'),
  /** errorReport頁面的UrlMatcher */
  errorReport: createStaticPathUrlMatcher('errorReport'),
  /** contactReport頁面的UrlMatcher */
  contactReport: createStaticPathUrlMatcher('contact'),
  /** 空字串頁面的UrlMatcher */
  empty: createStaticPathUrlMatcher(''),
  /** 需要取得path參數的頁面的UrlMatcher */
  ':path': createParameterizedPathUrlMatcher('path'),
  /** 需要取得lang參數的頁面的UrlMatcher */
  ':lang': createParameterizedPathUrlMatcher('lang'),
  /** 需要取得cmsType參數的頁面的UrlMatcher */
  ':cmsType': createParameterizedPathUrlMatcher('cmsType'),
  /** 需要取得id參數的頁面的UrlMatcher */
  ':id': createParameterizedPathUrlMatcher('id'),
  /** 需要取得literatureChapterId參數的頁面的UrlMatcher */
  ':literatureChapterId': createParameterizedPathUrlMatcher('literatureChapterId'),
  /** 需要取得path參數和lang參數的頁面的UrlMatcher */
  ':path/:lang': createParameterizedPathsUrlMatcher(['path', 'lang']),
} as const;

